import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles, Edit, History, Loader2, TrendingUp, TrendingDown, Minus,
  Lock, ShieldCheck, FileSpreadsheet, FileText, HeartPulse, HelpCircle,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { OwnerKpi, MetalChip, SummaryStrip } from "@/components/owner-reports/OwnerUI";
import { FILL } from "@/components/owner-reports/OwnerUI";
import { Wallet as EiWallet, CalendarDays, Scale, Percent as EiPercent, Crown, Layers, LayoutGrid as EiGrid, ChevronDown as EiDown, ChevronUp as EiUp } from "lucide-react";
const EI_FILL = { green: "emerald", amber: "gold", red: "ruby", blue: "blue", purple: "purple", neutral: "teal" };
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend,
} from "recharts";


import { resolveCurrency, formatMoney } from "../utils/money";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v, cur = "INR", digits = 0) => formatMoney(v, resolveCurrency(cur), { digits });
const currencySym = (cur) => resolveCurrency(cur).symbol;
const pct = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}%`);

const statusColor = (s) => ({
  "Excellent": "bg-emerald-500/20 text-emerald-700 border-emerald-500/40",
  "Within Market": "bg-sky-500/20 text-sky-700 border-sky-500/40",
  "Above Market": "bg-amber-500/20 text-amber-700 border-amber-500/40",
  "Needs Review": "bg-rose-500/20 text-rose-700 border-rose-500/40",
  // Franchise-owner (simplified) mode statuses
  "Higher than your normal average": "bg-rose-500/20 text-rose-700 border-rose-500/40",
  "Lower than your normal average": "bg-sky-500/20 text-sky-700 border-sky-500/40",
  "Stable": "bg-emerald-500/20 text-emerald-700 border-emerald-500/40",
}[s] || "bg-slate-500/20 text-slate-700 border-slate-500/40");

const CLASSIFICATIONS = ["Fixed", "Variable", "Semi-Variable", "Discretionary"];
const WINDOW_OPTIONS = [
  { value: "3", label: "Last 3 Months" },
  { value: "6", label: "Last 6 Months" },
  { value: "12", label: "Last 12 Months" },
  { value: "custom", label: "Custom Range" },
];

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp className="w-3.5 h-3.5 text-rose-600 inline" />;
  if (trend === "down") return <TrendingDown className="w-3.5 h-3.5 text-emerald-600 inline" />;
  return <Minus className="w-3.5 h-3.5 text-slate-400 inline" />;
};

const HealthDial = ({ score }) => {
  const color = score >= 80 ? "#059669" : score >= 60 ? "#0284c7" : score >= 40 ? "#d97706" : "#dc2626";
  const label = score >= 80 ? "Excellent" : score >= 60 ? "Healthy" : score >= 40 ? "Watch" : "Critical";
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 36 36" className="w-16 h-16">
          <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" fill="none" stroke="#e5e7eb" strokeWidth="3.5"/>
          <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" fill="none" stroke={color} strokeWidth="3.5"
                strokeDasharray={`${score}, 100`} strokeLinecap="round"/>
          <text x="18" y="21" textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>{score}</text>
        </svg>
      </div>
      <div className="text-[10px] font-semibold mt-1" style={{ color }}>{label}</div>
    </div>
  );
};

/**
 * Expense Intelligence & Average Cost Sheet — one component, three surfaces.
 *
 * Props:
 *   session       — auth session
 *   center, month — INITIAL filters (YYYY-MM). Once the user changes filters,
 *                   the component's INTERNAL state takes over — subsequent
 *                   parent `month` changes are ignored (per the "independent
 *                   date range" requirement).
 *   currency      — 'INR' | 'AUD'
 *   editable      — when true and user is admin, shows Edit + Audit + Delete.
 *                   Backend enforces role independently; UI hide is defence-in-depth.
 *   simplified    — franchise-owner mode: hide market/target/variance tiles,
 *                   drop those grid columns, and pass simplified=true to the
 *                   backend so Status + Recommendation compare against the
 *                   center's own historical average (never market data).
 *   storageKey    — unique key for persisting the tab's filters to
 *                   localStorage so switching between tabs (Monthly Reports ↔
 *                   Expense Intelligence) doesn't wipe user selections and
 *                   there is NO shared date state across surfaces. Examples:
 *                     'owner-ei', 'center-ei', 'cfr-ei'.
 */

// ═══════════════════════════════════════════════════════════════════════════
//  Monthly Sales & Expense Comparison — Detailed Current vs Average
//  Collapsible card rendered below the main EI grid.
// ═══════════════════════════════════════════════════════════════════════════
function MonthlyComparisonCard({ session, center, currency, currentMonth }) {
  const [open, setOpen] = useState(false);
  const currentYm = currentMonth || new Date().toISOString().slice(0, 7);
  const priorYm = (() => {
    const [y, m] = currentYm.split("-").map(Number);
    const d = new Date(y, m - 1 - 5, 1);   // 6-month window ending current
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [fromMonth, setFromMonth] = useState(priorYm);
  const [toMonth, setToMonth] = useState(currentYm);
  const [view, setView] = useState("both");     // table | chart | both
  const [selectedHeads, setSelectedHeads] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const num = (v) => Number(v || 0);
  const fmt = (v) => formatMoney(v, resolveCurrency(currency || "INR"));

  const load = useCallback(async () => {
    if (!open || !center || !fromMonth || !toMonth) return;
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/expense-intelligence/monthly-comparison`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session?.token, center,
          from_month: fromMonth, to_month: toMonth,
          heads: selectedHeads.length ? selectedHeads : null,
        }),
      });
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || d.detail || "Failed");
      setData(d);
    } catch (e) { setError(e.message); setData(null); }
    finally { setLoading(false); }
  }, [open, center, fromMonth, toMonth, selectedHeads, session?.token]);

  useEffect(() => { load(); }, [load]);

  const months = data?.months || [];
  const monthLabel = (m) => {
    const [y, mm] = m.split("-");
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(mm) - 1]} ${y.slice(2)}`;
  };

  const toggleHead = (h) => setSelectedHeads(s =>
    s.includes(h) ? s.filter(x => x !== h) : [...s, h]);
  const clearHeads = () => setSelectedHeads([]);
  const selectAll = () => setSelectedHeads([...(data?.available_heads || [])]);

  // ── Chart data — one row per month, series per selected head + Sales ──
  const chartData = useMemo(() => {
    if (!data) return [];
    return months.map(m => {
      const row = { month: monthLabel(m), Sales: num(data.sales.monthly[m]) };
      (data.heads || []).forEach(h => { row[h.head] = num(h.monthly[m]); });
      return row;
    });
  }, [data, months]);

  // Distinct color per head (deterministic).
  const palette = ["#8B0000", "#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6",
                   "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#EF4444"];

  const downloadCSV = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Head", ...months.map(monthLabel), "Total", "Monthly Avg", "% of Sales"]);
    const emit = (row) => rows.push([
      row.head,
      ...months.map(m => num(row.monthly[m]).toFixed(2)),
      num(row.total).toFixed(2),
      num(row.monthly_average).toFixed(2),
      `${num(row.pct_of_sales).toFixed(2)}%`,
    ]);
    emit(data.sales);
    (data.heads || []).forEach(emit);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sales-expense-comparison-${center}-${fromMonth}-to-${toMonth}.csv`;
    link.click();
  };

  return (
    <Card className="border-slate-200" data-testid="ei-monthly-comparison-card">
      <button type="button" onClick={() => setOpen(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              data-testid="ei-cmp-toggle">
        <span className="text-sm font-semibold text-slate-800">
          Detailed Current vs Average Analysis
          <span className="ml-2 text-[11px] font-normal text-slate-500">
            Monthly Sales &amp; Expense Comparison
          </span>
        </span>
        <span className="text-xs text-slate-500">{open ? "▲ Hide" : "▼ Show details"}</span>
      </button>
      {open && (
        <CardContent className="pt-0 space-y-3">
          <div className="flex flex-wrap items-end gap-2 pb-2 border-b border-slate-100">
            <div>
              <Label className="text-xs">From Month</Label>
              <Input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)}
                     className="h-8 w-36" data-testid="ei-cmp-from" />
            </div>
            <div>
              <Label className="text-xs">To Month</Label>
              <Input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)}
                     className="h-8 w-36" data-testid="ei-cmp-to" />
            </div>
            <div>
              <Label className="text-xs">View</Label>
              <select value={view} onChange={e => setView(e.target.value)}
                      className="h-8 text-xs border rounded px-2" data-testid="ei-cmp-view">
                <option value="table">Table</option>
                <option value="chart">Chart</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadCSV} disabled={!data}
                      data-testid="ei-cmp-export-csv" className="text-[11px]">Excel / CSV</Button>
              <Button size="sm" variant="outline" onClick={() => window.print()}
                      data-testid="ei-cmp-print" className="text-[11px]">Print / PDF</Button>
            </div>
          </div>

          {/* Head multi-select */}
          <div className="text-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-slate-700">Expense Heads ({selectedHeads.length} selected)</span>
              <Button size="sm" variant="ghost" onClick={selectAll} className="h-6 px-2 text-[10px]">Select all</Button>
              <Button size="sm" variant="ghost" onClick={clearHeads} className="h-6 px-2 text-[10px]">Clear</Button>
              <span className="text-[10px] text-slate-500 ml-auto">
                Empty = show all heads with data
              </span>
            </div>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto border border-slate-100 p-2 rounded"
                 data-testid="ei-cmp-heads">
              {(data?.available_heads || []).map(h => (
                <button key={h} type="button" onClick={() => toggleHead(h)}
                        className={`text-[10px] px-2 py-0.5 rounded border ${
                          selectedHeads.includes(h)
                            ? "bg-[#8B0000] text-white border-[#8B0000]"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}>
                  {h}
                </button>
              ))}
              {!data && !loading && <span className="text-slate-400 italic">Set date range to load heads…</span>}
            </div>
          </div>

          {loading && <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>}
          {error && <div className="p-3 text-center text-rose-700 text-xs">{error}</div>}

          {data && !loading && (
            <>
              {(view === "chart" || view === "both") && chartData.length > 0 && (
                <div className="border rounded p-2" data-testid="ei-cmp-chart">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="month" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="Sales" fill="#0EA5E9" />
                      {(data.heads || []).map((h, i) => (
                        <Bar key={h.head} dataKey={h.head} fill={palette[(i + 1) % palette.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {(view === "table" || view === "both") && (
                <div className="overflow-x-auto border rounded max-h-[60vh]" data-testid="ei-cmp-table">
                  <Table className="text-xs">
                    <TableHeader className="bg-slate-100 sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="min-w-[180px]">Head</TableHead>
                        {months.map(m => (
                          <TableHead key={m} className="text-right whitespace-nowrap">{monthLabel(m)}</TableHead>
                        ))}
                        <TableHead className="text-right font-bold">Total</TableHead>
                        <TableHead className="text-right">Monthly Avg</TableHead>
                        <TableHead className="text-right">% of Sales</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="bg-sky-50 font-semibold border-b border-sky-300">
                        <TableCell>SALES AMOUNT</TableCell>
                        {months.map(m => (
                          <TableCell key={m} className="text-right font-mono">{fmt(data.sales.monthly[m])}</TableCell>
                        ))}
                        <TableCell className="text-right font-mono">{fmt(data.sales.total)}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(data.sales.monthly_average)}</TableCell>
                        <TableCell className="text-right">100.00%</TableCell>
                      </TableRow>

                      {/* SELECTED EXPENSE TOTAL — appears immediately below
                          Sales when at least one head is selected. Sums only
                          the selected heads for each month, using the exact
                          month count as divisor for the Monthly Average. */}
                      {selectedHeads.length > 0 && (data.heads || []).length > 0 && (() => {
                        const monthlySum = {};
                        months.forEach(m => {
                          monthlySum[m] = (data.heads || []).reduce(
                            (acc, h) => acc + num(h.monthly?.[m]), 0);
                        });
                        const total = (data.heads || []).reduce((acc, h) => acc + num(h.total), 0);
                        const avg = months.length ? total / months.length : 0;
                        const pct = num(data.sales.total) > 0 ? (total / num(data.sales.total)) * 100 : 0;
                        return (
                          <TableRow className="bg-amber-50 font-bold border-y-2 border-amber-400"
                                    data-testid="ei-cmp-selected-total">
                            <TableCell>SELECTED EXPENSE TOTAL</TableCell>
                            {months.map(m => (
                              <TableCell key={m} className="text-right font-mono">{fmt(monthlySum[m])}</TableCell>
                            ))}
                            <TableCell className="text-right font-mono">{fmt(total)}</TableCell>
                            <TableCell className="text-right font-mono">{fmt(avg)}</TableCell>
                            <TableCell className="text-right">{pct.toFixed(2)}%</TableCell>
                          </TableRow>
                        );
                      })()}

                      {(data.heads || []).map(h => (
                        <TableRow key={h.head}>
                          <TableCell className="font-medium">{h.head}</TableCell>
                          {months.map(m => (
                            <TableCell key={m} className="text-right font-mono text-slate-700">
                              {num(h.monthly[m]) ? fmt(h.monthly[m]) : <span className="text-slate-300">—</span>}
                            </TableCell>
                          ))}
                          <TableCell className="text-right font-mono font-semibold">{fmt(h.total)}</TableCell>
                          <TableCell className="text-right font-mono">{fmt(h.monthly_average)}</TableCell>
                          <TableCell className="text-right">{h.pct_of_sales.toFixed(2)}%</TableCell>
                        </TableRow>
                      ))}

                      {/* SELECTED EXPENSE % OF SALES — per-month ratio row */}
                      {selectedHeads.length > 0 && (data.heads || []).length > 0 && (() => {
                        const pctByMonth = {};
                        months.forEach(m => {
                          const s = num(data.sales.monthly?.[m]);
                          const e = (data.heads || []).reduce(
                            (acc, h) => acc + num(h.monthly?.[m]), 0);
                          pctByMonth[m] = s > 0 ? (e / s) * 100 : 0;
                        });
                        const totalE = (data.heads || []).reduce((acc, h) => acc + num(h.total), 0);
                        const totalPct = num(data.sales.total) > 0 ? (totalE / num(data.sales.total)) * 100 : 0;
                        const avgPct = months.length ? Object.values(pctByMonth).reduce((a, b) => a + b, 0) / months.length : 0;
                        return (
                          <TableRow className="bg-slate-50 font-semibold border-t border-slate-300"
                                    data-testid="ei-cmp-selected-pct">
                            <TableCell>SELECTED EXPENSE % OF SALES</TableCell>
                            {months.map(m => (
                              <TableCell key={m} className="text-right">{pctByMonth[m].toFixed(2)}%</TableCell>
                            ))}
                            <TableCell className="text-right font-semibold">{totalPct.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{avgPct.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">—</TableCell>
                          </TableRow>
                        );
                      })()}

                      {(data.heads || []).length === 0 && (
                        <TableRow><TableCell colSpan={months.length + 4} className="text-center py-6 text-slate-500 text-xs">
                          Select one or more expense heads above to see comparison rows.
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}


export default function ExpenseIntelligenceTab({
  session, center, month, currency = "INR",
  editable = false, simplified = false, storageKey = "shared-ei", strip = false,
}) {
  const [headFilter, setHeadFilter] = useState("");
  const eiReq = useRef(0);
  const [showZeroHeads, setShowZeroHeads] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // ─── Independent, per-surface filter state ────────────────────
  // Initialised from prop `month` on FIRST mount, then decoupled from the
  // parent's month state. Persisted to localStorage so tab-switching
  // preserves the user's selection.
  const _storageK = `ei-filters:${storageKey}`;
  const _restored = (() => {
    try {
      const raw = typeof window !== "undefined" && window.localStorage.getItem(_storageK);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [avgWindow, setAvgWindow] = useState(_restored?.avgWindow ?? "3");
  const [customStart, setCustomStart] = useState(_restored?.customStart ?? "");
  const [customEnd, setCustomEnd] = useState(_restored?.customEnd ?? "");
  const [avgFromDate, setAvgFromDate] = useState(_restored?.avgFromDate ?? "");
  const [avgToDate, setAvgToDate] = useState(_restored?.avgToDate ?? "");
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sort, setSort] = useState({ key: "amount", dir: "desc" });
  const [exporting, setExporting] = useState(null);

  // Diagnostics (admin only)
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [avgDiagOpen, setAvgDiagOpen] = useState(false);
  const [avgDiagData, setAvgDiagData] = useState(null);
  const [avgDiagLoading, setAvgDiagLoading] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);

  // ── Collapsible outer wrap (default: collapsed). Persisted per storageKey
  // so switching between Center Accounts / Owner Reports / Sales Dashboard
  // remembers the user's preference within a session.
  const collapsedKey = `ei-collapsed-${storageKey}`;
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem(collapsedKey) !== "false";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(collapsedKey, String(collapsed));
    }
  }, [collapsed, collapsedKey]);

  const [repairData, setRepairData] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);

  const isAdmin = Boolean(session?.is_super_admin || session?.is_admin);
  const canEdit = editable && isAdmin && !simplified;
  const canDiagnose = isAdmin && !simplified;

  // Persist filter changes
  const persistRef = useRef(0);
  useEffect(() => {
    persistRef.current += 1;
    if (persistRef.current === 1) return; // skip initial
    try {
      window.localStorage.setItem(_storageK, JSON.stringify({
        avgWindow, customStart, customEnd, avgFromDate, avgToDate,
      }));
    } catch { /* localStorage unavailable */ }
  }, [_storageK, avgWindow, customStart, customEnd, avgFromDate, avgToDate]);

  const load = useCallback(async () => {
    if (!session?.token || !center || !month) return;
    const reqId = ++eiReq.current;
    setLoading(true); setData(null);
    try {
      const body = { token: session.token, center, month, simplified };
      if (avgWindow === "custom") {
        if (avgFromDate && avgToDate) {
          body.custom_start_month = avgFromDate.slice(0, 7);
          body.custom_end_month = avgToDate.slice(0, 7);
        } else if (customStart && customEnd) {
          body.custom_start_month = customStart;
          body.custom_end_month = customEnd;
        } else {
          body.avg_window = 3;
        }
      } else {
        body.avg_window = parseInt(avgWindow, 10);
      }
      const res = await fetch(`${API}/api/expense-intelligence/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("failed");
      const d = await res.json();
      if (reqId === eiReq.current) setData(d);
    } catch { if (reqId === eiReq.current) toast.error("Failed to load Expense Intelligence"); }
    finally { if (reqId === eiReq.current) setLoading(false); }
  }, [session?.token, center, month, simplified,
      avgWindow, customStart, customEnd, avgFromDate, avgToDate]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const allRows = data?.rows || [];
  const classifications = useMemo(
    () => Array.from(new Set(allRows.map(r => r.classification).filter(Boolean))).sort(),
    [allRows],
  );
  const num = (v) => Number(v || 0);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = allRows.filter((r) => {
      if (headFilter && r.head !== headFilter) return false;
      if (q && !(r.head || "").toLowerCase().includes(q)) return false;
      if (classFilter && String(r.classification || "").toLowerCase() !== classFilter.toLowerCase()) return false;
      return true;
    });
    const key = sort.key;
    const dir = sort.dir === "desc" ? -1 : 1;
    const numericKeys = new Set([
      "amount", "actual_pct", "window_avg", "delta_pct_vs_window",
      "market_std_pct", "approved_target_pct", "variance_amount",
      "variance_pct", "cost_per_guest", "cost_per_bill",
    ]);
    const derive = (r) => {
      if (numericKeys.has(key)) return num(r[key]);
      if (key === "trend") return r.trend || "";
      return String(r[key] || "");
    };
    return [...base].sort((a, b) => {
      const av = derive(a); const bv = derive(b);
      if (numericKeys.has(key)) return (num(av) - num(bv)) * dir;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
    });
  }, [allRows, search, classFilter, sort, headFilter]);

  // TOTAL row values — recalculated across *visible* (filter+sort) rows only.
  const visibleTotals = useMemo(() => {
    const acc = {
      amount: 0, window_avg: 0, variance_amount: 0,
      cost_per_guest_num: 0,  // will be avg / weighted below
      cost_per_bill_num: 0,
      market_wsum: 0, target_wsum: 0, weight_amount: 0,
    };
    for (const r of rows) {
      acc.amount += num(r.amount);
      acc.window_avg += num(r.window_avg);
      acc.variance_amount += num(r.variance_amount);
      // For Market % / Target % totals use amount-weighted average (matches
      // Actual % which is Total Amount ÷ Total Sales).
      acc.market_wsum += num(r.market_std_pct) * num(r.amount);
      acc.target_wsum += num(r.approved_target_pct) * num(r.amount);
      acc.weight_amount += num(r.amount);
    }
    const salesTotal = num(data?.sales_total);
    const guests = num(data?.total_guests);
    const bills = num(data?.total_bills);
    return {
      amount: acc.amount,
      actual_pct: salesTotal > 0 ? (acc.amount / salesTotal) * 100 : 0,
      window_avg: acc.window_avg,
      difference: acc.amount - acc.window_avg,
      market_pct: acc.weight_amount > 0 ? acc.market_wsum / acc.weight_amount : 0,
      target_pct: acc.weight_amount > 0 ? acc.target_wsum / acc.weight_amount : 0,
      variance_amount: acc.variance_amount,
      variance_pct: acc.window_avg > 0 ? (acc.variance_amount / acc.window_avg) * 100 : 0,
      cost_per_guest: guests > 0 ? acc.amount / guests : 0,
      cost_per_bill: bills > 0 ? acc.amount / bills : 0,
    };
  }, [rows, data]);

  const toggleSort = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  const arrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const openAvgDiag = async () => {
    if (!isAdmin) return;
    setAvgDiagOpen(true); setAvgDiagData(null); setAvgDiagLoading(true);
    try {
      const res = await fetch(`${API}/api/expense-intelligence/avg_diagnostic`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center, month,
          avg_window: avgWindow === "custom" ? 12 : parseInt(avgWindow, 10),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "failed");
      setAvgDiagData(d);
    } catch (e) { toast.error(e.message || "Diagnostic failed"); }
    finally { setAvgDiagLoading(false); }
  };

  const openDiagnose = async (head) => {
    if (!canDiagnose) return;
    setDiagOpen(true); setDiagData(null); setDiagLoading(true);
    try {
      const res = await fetch(`${API}/api/expense-intelligence/diagnose-head`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token, center, head, month,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Diagnose failed");
      setDiagData(d);
    } catch (e) { toast.error(e.message); }
    finally { setDiagLoading(false); }
  };

  const runRepair = async () => {
    if (!isAdmin) return;
    setRepairOpen(true); setRepairData(null); setRepairLoading(true);
    try {
      const res = await fetch(`${API}/api/expense-intelligence/repair-heads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Repair scan failed");
      setRepairData(d);
    } catch (e) { toast.error(e.message); }
    finally { setRepairLoading(false); }
  };

  const openEdit = (row) => {
    setEditRow({
      head: row.head,
      benchmark_pct: row.benchmark_pct,
      market_std_pct: row.market_std_pct,
      approved_target_pct: row.approved_target_pct,
      classification: row.classification,
      remarks: row.remarks || "",
      reason: "",
    });
  };

  const saveEdit = async () => {
    if (!editRow.reason) { toast.error("Please enter a reason (audit trail)"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/expense-intelligence/upsert`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, ...editRow }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "save failed");
      toast.success(`Updated ${editRow.head} · ${d.audit_rows_written} audit row(s) written`);
      setEditRow(null);
      await load();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await fetch(`${API}/api/expense-intelligence/audit-trail`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, limit: 200 }),
      });
      const d = await res.json();
      setAuditRows(d.rows || []);
    } catch { toast.error("Failed to load audit trail"); }
    finally { setAuditLoading(false); }
  };

  const doExport = async (fmtType) => {
    setExporting(fmtType);
    try {
      const body = { token: session.token, center, month, format: fmtType };
      if (avgWindow === "custom" && customStart && customEnd) {
        body.custom_start_month = customStart; body.custom_end_month = customEnd;
      } else {
        body.avg_window = parseInt(avgWindow === "custom" ? "3" : avgWindow, 10);
      }
      const res = await fetch(`${API}/api/expense-intelligence/export`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `expense_intelligence_${center}_${month}.${fmtType === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmtType.toUpperCase()}`);
    } catch { toast.error(`Failed to export ${fmtType}`); }
    finally { setExporting(null); }
  };

  const diffPositive = (summary.difference_amount || 0) >= 0;

  if (!center || !month) {
    return <Card><CardContent className="p-6 text-center text-slate-500">Select a centre + month.</CardContent></Card>;
  }

  const hs = summary.expense_health_score ?? 0;
  const hsLabel = hs >= 80 ? "Excellent" : hs >= 60 ? "Watch" : "Action Needed";
  const topHead = allRows.reduce((m, r) => (num(r.amount) > num(m?.amount) ? r : m), null);
  const metrics = data ? [
    { k: "avg", label: "Avg. Monthly Expense", value: fmt(summary.avg_total_expenses, currency), sub: `Based on ${summary.avg_window_label || "—"}`, tone: "blue", Icon: EiWallet },
    { k: "current", label: "Current Month", value: fmt(data.expenses_total, currency), sub: `${pct(summary.total_expense_pct)} of sales`, tone: "purple", Icon: CalendarDays },
    { k: "diff", label: "Difference", value: `${diffPositive ? "+" : ""}${fmt(summary.difference_amount, currency)}`, sub: diffPositive ? "Above average" : "Below average", tone: diffPositive ? "red" : "green", Icon: Scale },
    { k: "diff-pct", label: "Difference %", value: `${diffPositive ? "+" : ""}${pct(summary.difference_pct)}`, sub: "vs average", tone: diffPositive ? "red" : "green", Icon: EiPercent },
    { k: "health", label: "Expense Health", value: `${hs} / 100`, sub: hsLabel, tone: hs >= 80 ? "green" : hs >= 60 ? "amber" : "red", Icon: HeartPulse },
    { k: "top", label: "Top Expense Head", value: topHead?.head || "—", sub: topHead ? `${fmt(topHead.amount, currency)} · ${pct(topHead.actual_pct)} of sales` : "", tone: "amber", Icon: Crown },
  ] : [];
  const pickHead = (h) => {
    setHeadFilter(h); setCollapsed(false);
    setTimeout(() => document.querySelector(`[data-testid="ei-count"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };
  const zeroHeads = allRows.filter((r) => !num(r.amount)).length;
  const shownHeads = showZeroHeads ? allRows : allRows.filter((r) => num(r.amount) || r.head === headFilter);
  const HEAD_FILL = { up: FILL.gold, down: FILL.emerald, flat: FILL.blue, all: FILL.purple };
  const catBlocks = allRows.length > 0 && (
    <div className="space-y-2" data-testid="ei-head-blocks">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-bold text-[#750817] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}><Layers className="w-5 h-5" /> Expense Heads</h3>
        <div className="flex gap-2 flex-wrap">
          {zeroHeads > 0 && <button type="button" className="bi-pill" onClick={() => setShowZeroHeads(!showZeroHeads)} data-testid="ei-head-zero-toggle">{showZeroHeads ? "Hide zero-amount heads" : `Show all heads (+${zeroHeads} at zero)`}</button>}
          {headFilter && <button type="button" className="bi-pill" onClick={() => setHeadFilter("")} data-testid="ei-head-all-btn">All Expenses</button>}
        </div>
      </div>
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {[{ head: "", amount: data?.expenses_total }, ...shownHeads].map((r, i) => {
          const active = headFilter === r.head;
          const up = num(r.delta_pct_vs_window) > 0;
          return (
            <button key={r.head || "__all"} type="button" onClick={() => pickHead(r.head)} aria-pressed={active}
              className={`bi-tile !min-h-[118px] gap-0.5 ${active ? "ring-4 ring-[#750817] ring-offset-2 ring-offset-[#fffaf0] scale-[1.03]" : ""}`}
              style={{ "--bi-fill": HEAD_FILL[!r.head ? "all" : num(r.delta_pct_vs_window) > 0 ? "up" : num(r.delta_pct_vs_window) < 0 ? "down" : "flat"], animationDelay: `${Math.min(i, 12) * 25}ms` }} data-testid={`ei-head-block-${(r.head || "all").replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <span className="text-base font-bold leading-tight break-words">{r.head || "All Expenses"}</span>
              <span className="font-mono text-lg font-bold">{fmt(r.amount, currency)}</span>
              {r.head ? <>
                <span className="text-xs opacity-90">Avg {fmt(r.window_avg, currency)}</span>
                <span className="text-xs font-bold">{up ? "▲ +" : "▼ "}{pct(r.delta_pct_vs_window)} · {r.status || ""}</span>
              </> : <span className="text-xs opacity-90">{allRows.length} heads · current month</span>}
            </button>);
        })}
      </div>
    </div>
  );

  const body = (
    <div className="space-y-4" data-testid="expense-intelligence-tab">
      {!strip && !data && (
        <div className="rounded-xl border-2 border-[#e2c27a] bg-[#fffdf8] p-4 text-base text-[#5a3a22] flex items-center gap-2" data-testid="ei-loading">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Loading Expense Intelligence…</> : "No expense data for this period."}
        </div>
      )}
      {!strip && data && (
        <div className="grid grid-cols-2 md:grid-cols-3 min-[1500px]:grid-cols-6 gap-3" data-testid="ei-metal-blocks">
          {metrics.map((m, i) => <OwnerKpi key={m.k} i={i} fill={EI_FILL[m.tone]} Icon={m.Icon} label={m.label} value={m.value} sub={m.sub} testid={`ei-block-${m.k}`} />)}
        </div>
      )}
      {!strip && catBlocks}
      {!strip && (
        <button type="button" onClick={() => setCollapsed(c => !c)} className="flex w-full items-center gap-2 rounded-xl border-2 border-[#e2c27a] bg-[#fffdf8] px-4 py-3 text-left text-lg font-bold text-[#750817]" data-testid="ei-details-toggle">
          <EiGrid className="w-5 h-5" /><span className="flex-1">Expense Head Details (Grid){headFilter ? ` — ${headFilter}` : ""}</span>
          <span className="text-sm font-semibold text-[#5a3a22]">{collapsed ? "View Details" : "Hide Details"}</span>{collapsed ? <EiDown className="w-5 h-5" /> : <EiUp className="w-5 h-5" />}
        </button>
      )}
      {strip && catBlocks}
      {/* ── Collapsible Summary Header (always visible) ─────────────── */}
      <Card className="border-slate-200 hidden" data-testid="ei-collapsible-header">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
          data-testid="ei-collapse-toggle"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-4 h-4 text-[#8B0000] shrink-0" />
              <span className="text-sm font-bold text-slate-800">Expense Intelligence – Detailed View</span>
              {canEdit ? (
                <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40 text-[10px]">
                  <ShieldCheck className="w-3 h-3 mr-0.5" /> Editable
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">
                  <Lock className="w-3 h-3 mr-0.5" /> Read-only
                </Badge>
              )}
            </div>
            {loading ? (
              <p className="text-[11px] text-slate-500 mt-1">Loading…</p>
            ) : data ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-slate-600">
                <span>Center: <span className="font-semibold text-slate-800">{center || "—"}</span></span>
                <span>Month: <span className="font-semibold text-slate-800">{month || "—"}</span></span>
                <span>Current: <span className="font-mono font-semibold text-slate-800">{fmt(summary.current_total_expenses, currency)}</span></span>
                <span>Average: <span className="font-mono font-semibold text-slate-800">{fmt(summary.avg_total_expenses, currency)}</span></span>
                <span className={diffPositive ? "text-rose-700" : "text-emerald-700"}>
                  Variance: <span className="font-mono font-semibold">{diffPositive ? "+" : ""}{fmt(summary.difference_amount, currency)}</span>
                  {" "}({diffPositive ? "+" : ""}{pct(summary.difference_pct)})
                </span>
                <Badge className={
                  (summary.expense_health_score ?? 0) >= 80 ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  (summary.expense_health_score ?? 0) >= 60 ? "bg-amber-100 text-amber-800 border-amber-300" :
                  "bg-rose-100 text-rose-800 border-rose-300"
                }>
                  Status: {
                    (summary.expense_health_score ?? 0) >= 80 ? "Excellent" :
                    (summary.expense_health_score ?? 0) >= 60 ? "Watch" : "Action Needed"
                  }
                </Badge>
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1">No data for this period.</p>
            )}
          </div>
          <span className="text-xs text-slate-500 whitespace-nowrap ml-2">
            {collapsed ? "▼ Expand" : "▲ Collapse"}
          </span>
        </button>
      </Card>

      {!collapsed && (
      <>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8B0000]" /> Expense Intelligence &amp; Average Cost Sheet
            {canEdit ? (
              <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40 text-xs">
                <ShieldCheck className="w-3 h-3 mr-1" /> Editable — Admin
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                <Lock className="w-3 h-3 mr-1" /> Read-only
              </Badge>
            )}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {simplified
              ? <>Your center · {rows.length} of {allRows.length} heads · Sales {fmt(data?.sales_total, currency)}</>
              : <>Canonical benchmarks · {rows.length} heads · Sales {fmt(data?.sales_total, currency)}
                · Guests {data?.total_guests || 0} · Bills {data?.total_bills || 0}</>}
          </p>
          {/* Current Period + Average chips — Current Month is ALWAYS the
              full accounting month; no date-range picker is exposed. */}
          <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="ei-current-period-chip">
            <Badge variant="outline" className="text-[11px] bg-slate-50 border-slate-300">
              Current Month: <span className="font-semibold ml-1">{month || "—"}</span>
            </Badge>
            <Badge variant="outline" className="text-[11px] bg-slate-50 border-slate-300">
              Average: <span className="font-semibold ml-1">{summary.avg_window_label || "Last 3 Months"}</span>
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs text-slate-600 whitespace-nowrap">Average Based On</Label>
          <Select value={avgWindow} onValueChange={setAvgWindow}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="ei-avg-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {avgWindow === "custom" && (
            <>
              <Label className="text-xs text-slate-600">From</Label>
              <Input type="date" value={avgFromDate} onChange={(e) => setAvgFromDate(e.target.value)}
                     className="h-8 text-xs w-36" data-testid="ei-avg-from-date" placeholder="Avg From" />
              <Label className="text-xs text-slate-600">To</Label>
              <Input type="date" value={avgToDate} onChange={(e) => setAvgToDate(e.target.value)}
                     className="h-8 text-xs w-36" data-testid="ei-avg-to-date" placeholder="Avg To" />
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => doExport("excel")} disabled={!!exporting} data-testid="ei-export-excel">
            {exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            <span className="ml-1">Excel</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="ei-export-pdf">
            {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            <span className="ml-1">PDF</span>
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={openAudit} data-testid="ei-audit-btn">
              <History className="w-4 h-4 mr-1" /> Audit
            </Button>
          )}
          {isAdmin && !simplified && (
            <Button variant="outline" size="sm" onClick={runRepair} data-testid="ei-repair-btn">
              <Wrench className="w-4 h-4 mr-1" /> Repair Heads
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm"
                    onClick={() => setReconOpen(true)} data-testid="ei-reconcile-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> Reconcile
            </Button>
          )}
        </div>
      </div>

      {loading && !data && (
        <Card><CardContent className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></CardContent></Card>
      )}

      {data && (
        <>
          {/* ── BIG Average Monthly Expenses Tile ─────────────────── */}
          <Card className="border-[#8B0000]/30 bg-gradient-to-br from-[#8B0000]/5 via-white to-amber-50 shadow-sm"
                data-testid="ei-avg-hero">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                <div className="md:col-span-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Average Monthly Expenses</p>
                  <p className="text-4xl md:text-5xl font-black text-[#8B0000] mt-1 tracking-tight" data-testid="ei-avg-total-amount">
                    {fmt(summary.avg_total_expenses, currency)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Based on {summary.avg_window_label}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Current Month</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="ei-current-total">
                    {fmt(data.expenses_total, currency)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">{pct(summary.total_expense_pct)} of sales</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Difference</p>
                  <p className={`text-2xl font-bold mt-1 flex items-center gap-1 ${diffPositive ? "text-rose-600" : "text-emerald-600"}`}
                     data-testid="ei-difference">
                    {diffPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                    {diffPositive ? "+" : ""}{fmt(summary.difference_amount, currency)}
                  </p>
                  <p className={`text-[11px] mt-1 font-semibold ${diffPositive ? "text-rose-600" : "text-emerald-600"}`}>
                    {diffPositive ? "+" : ""}{pct(summary.difference_pct)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Summary Tiles Row ─────────────────────────────────── */}
          {simplified ? (
            /* Franchise-owner mode: only Expense Health tile remains
               (Average / Current / Difference are already in the hero card above). */
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="ei-summary-tiles">
              <Card className="border-slate-200 bg-white md:col-span-4" data-testid="tile-health">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                      <HeartPulse className="w-3 h-3" /> Expense Health
                    </p>
                    <p className="text-[10px] text-slate-500 mt-2">Compares current month vs your own historical average.</p>
                  </div>
                  <HealthDial score={summary.expense_health_score ?? 0} />
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="ei-summary-tiles">
              <SummaryTile label="Total Expenses" value={fmt(data.expenses_total, currency)} tone="rose" testid="tile-total-exp" />
              <SummaryTile label="Avg Total Exp" value={fmt(summary.avg_total_expenses, currency)} tone="amber" testid="tile-avg-exp" />
              <SummaryTile label="Total Exp %" value={pct(summary.total_expense_pct)} tone="sky" testid="tile-total-pct" />
              <SummaryTile label="Avg Exp %" value={pct(summary.avg_expense_pct)} tone="indigo" testid="tile-avg-pct" />
              <SummaryTile label="Market Std %" value={pct(summary.market_std_total_pct)} tone="slate" testid="tile-market-pct" />
              <SummaryTile label={`Total Variance ${currencySym(currency)}`}
                           value={fmt(summary.total_variance_amount, currency)}
                           tone={(summary.total_variance_amount || 0) > 0 ? "rose" : "emerald"}
                           testid="tile-variance-amt" />
              <SummaryTile label="Total Variance %"
                           value={pct(summary.total_variance_pct)}
                           tone={(summary.total_variance_pct || 0) > 0 ? "rose" : "emerald"}
                           testid="tile-variance-pct" />
              <Card className="border-slate-200 bg-white" data-testid="tile-health">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                      <HeartPulse className="w-3 h-3" /> Health
                    </p>
                    <p className="text-[10px] text-slate-500 mt-2">Score</p>
                  </div>
                  <HealthDial score={summary.expense_health_score ?? 0} />
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Totals Row (Management only — hides market/target/variance in simplified mode) ── */}
          {!simplified && (
            <Card className="border-slate-300 bg-slate-50/60" data-testid="ei-totals-row">
              <CardContent className="p-3">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                  <TotalCell label="Current Total Expenses" value={fmt(data.expenses_total, currency)} bold />
                  <TotalCell label="Average Total Expenses" value={fmt(summary.avg_total_expenses, currency)} />
                  <TotalCell label="Market Target" value={fmt(summary.market_target_amount, currency)} sub={pct(summary.market_std_total_pct)} />
                  <TotalCell label="Approved Target" value={fmt(summary.approved_target_amount, currency)} sub={pct(summary.approved_target_total_pct)} />
                  <TotalCell label={`Variance ${currencySym(currency)}`}
                             value={fmt(summary.total_variance_amount, currency)}
                             tone={(summary.total_variance_amount || 0) > 0 ? "rose" : "emerald"}
                             sub={pct(summary.total_variance_pct)} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Search + filters + admin diagnostic ───────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            {headFilter && <Badge className="bg-[#750817] text-[#fff3c4] text-sm" data-testid="ei-head-filter-badge">Head: {headFilter}
              <button type="button" className="ml-2" onClick={() => setHeadFilter("")} data-testid="ei-head-filter-clear">×</button></Badge>}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search expense head — e.g. Grocery, Electricity…"
              className="h-9 max-w-xs"
              data-testid="ei-search"
            />
            {search && (
              <Button size="sm" variant="ghost" onClick={() => setSearch("")} data-testid="ei-search-clear">
                Clear
              </Button>
            )}
            {!simplified && classifications.length > 0 && (
              <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                      className="h-8 text-xs border rounded px-2" data-testid="ei-class-filter">
                <option value="">All classes</option>
                {classifications.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={openAvgDiag}
                      data-testid="ei-avg-diagnostic"
                      className="text-[11px]">
                View Average Calculation
              </Button>
            )}
            <p className="text-[11px] text-slate-500 ml-auto" data-testid="ei-count">
              Showing {rows.length} of {allRows.length} Expense Heads
            </p>
          </div>

          {/* ── Average Cost Sheet Grid ────────────────────────────── */}
          {allRows.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">
              No expense entries recorded for this month.
            </CardContent></Card>
          ) : rows.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="ei-no-search-results">
              No expense head matches the current search / filter.
              <Button size="sm" variant="link" onClick={() => { setSearch(""); setClassFilter(""); }}>Clear</Button>
            </CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <Table className="text-xs">
                    <TableHeader className="sticky top-0 z-20 bg-slate-100 shadow-sm">
                      <TableRow className="bg-slate-100">
                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("head")}>Head{arrow("head")}</TableHead>
                        <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("amount")}>
                          {simplified ? "Current Month" : "Amount"}{arrow("amount")}
                        </TableHead>
                        <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("actual_pct")}>Actual %{arrow("actual_pct")}</TableHead>
                        <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("window_avg")} data-testid="ei-col-avg">
                          {summary.avg_window_label ? summary.avg_window_label.replace("Last ", "") : "Avg"} Avg{arrow("window_avg")}
                        </TableHead>
                        <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("delta_pct_vs_window")}>Difference{arrow("delta_pct_vs_window")}</TableHead>
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("market_std_pct")}>Market %{arrow("market_std_pct")}</TableHead>}
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("approved_target_pct")}>Target %{arrow("approved_target_pct")}</TableHead>}
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("variance_amount")}>Variance {currencySym(currency)}{arrow("variance_amount")}</TableHead>}
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("variance_pct")}>Variance %{arrow("variance_pct")}</TableHead>}
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("cost_per_guest")}>{currencySym(currency)}/Guest{arrow("cost_per_guest")}</TableHead>}
                        {!simplified && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("cost_per_bill")}>{currencySym(currency)}/Bill{arrow("cost_per_bill")}</TableHead>}
                        <TableHead className="cursor-pointer select-none text-center" onClick={() => toggleSort("trend")}>Trend{arrow("trend")}</TableHead>
                        {!simplified && <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("classification")}>Class{arrow("classification")}</TableHead>}
                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("status")}>Status{arrow("status")}</TableHead>
                        <TableHead>Recommendation</TableHead>
                        {canEdit && <TableHead />}
                      </TableRow>
                      {/* TOTAL row — pinned directly below headers, recalculates on filter/search/sort */}
                      <TableRow className="bg-amber-50 font-bold border-b-2 border-amber-300 sticky top-[36px] z-10" data-testid="ei-grid-totals">
                        <TableCell className="font-bold text-slate-800">TOTAL</TableCell>
                        <TableCell className="text-right font-mono">{fmt(visibleTotals.amount, currency)}</TableCell>
                        <TableCell className="text-right">{pct(visibleTotals.actual_pct)}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(visibleTotals.window_avg, currency)}</TableCell>
                        <TableCell className={`text-right ${visibleTotals.difference > 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(visibleTotals.difference, currency)}</TableCell>
                        {!simplified && <TableCell className="text-right">{pct(visibleTotals.market_pct)}</TableCell>}
                        {!simplified && <TableCell className="text-right">{pct(visibleTotals.target_pct)}</TableCell>}
                        {!simplified && <TableCell className={`text-right font-mono ${visibleTotals.variance_amount > 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(visibleTotals.variance_amount, currency)}</TableCell>}
                        {!simplified && <TableCell className={`text-right ${visibleTotals.variance_pct > 0 ? "text-rose-700" : "text-emerald-700"}`}>{pct(visibleTotals.variance_pct)}</TableCell>}
                        {!simplified && <TableCell className="text-right">{fmt(visibleTotals.cost_per_guest, currency)}</TableCell>}
                        {!simplified && <TableCell className="text-right">{fmt(visibleTotals.cost_per_bill, currency)}</TableCell>}
                        <TableCell className="text-center">—</TableCell>
                        {!simplified && <TableCell>—</TableCell>}
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                        {canEdit && <TableCell />}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.head}
                                  data-testid={`ei-row-${r.head.replace(/\s+/g, "-").toLowerCase()}`}>
                          <TableCell className="font-semibold whitespace-nowrap">
                            {r.head}
                            {r.is_override && !simplified && <Badge variant="outline" className="ml-1 text-[9px]">Override</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-mono">{fmt(r.amount, currency)}</TableCell>
                          <TableCell className="text-right">{r.actual_pct.toFixed(2)}%</TableCell>
                          <TableCell className="text-right text-slate-600 font-mono">{fmt(r.window_avg, currency)}</TableCell>
                          <TableCell className={`text-right ${r.delta_pct_vs_window > 0 ? "text-rose-700" : r.delta_pct_vs_window < 0 ? "text-emerald-700" : "text-slate-600"}`}>
                            {r.delta_pct_vs_window > 0 ? "+" : ""}{(r.delta_pct_vs_window || 0).toFixed(1)}%
                          </TableCell>
                          {!simplified && <TableCell className="text-right">{r.market_std_pct}%</TableCell>}
                          {!simplified && <TableCell className="text-right">{r.approved_target_pct}%</TableCell>}
                          {!simplified && <TableCell className={`text-right font-medium ${r.variance_amount > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {fmt(r.variance_amount, currency)}
                          </TableCell>}
                          {!simplified && <TableCell className={`text-right ${r.variance_pct > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%
                          </TableCell>}
                          {!simplified && <TableCell className="text-right">{fmt(r.cost_per_guest, currency)}</TableCell>}
                          {!simplified && <TableCell className="text-right">{fmt(r.cost_per_bill, currency)}</TableCell>}
                          <TableCell className="text-center"><TrendIcon trend={r.trend} /></TableCell>
                          {!simplified && <TableCell className="whitespace-nowrap">{r.classification}</TableCell>}
                          <TableCell><Badge className={statusColor(r.status)}>{r.status}</Badge></TableCell>
                          <TableCell className="text-slate-600 min-w-[220px]">
                            {r.recommendation}
                            {canDiagnose && (r.amount || 0) === 0 && (
                              <Button size="sm" variant="ghost"
                                      className="ml-1 h-6 px-1 text-slate-500 hover:text-[#8B0000]"
                                      onClick={() => openDiagnose(r.head)}
                                      data-testid={`ei-diagnose-${r.head.replace(/\s+/g, "-").toLowerCase()}`}
                                      title="Why is this 0?">
                                <HelpCircle className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                                      data-testid={`ei-edit-${r.head.replace(/\s+/g, "-").toLowerCase()}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── Detailed Current vs Average Analysis (collapsible) ─────────── */}
      </>
      )}
      {/* Collapse Detailed View footer button (visible when expanded) */}
      {!collapsed && (
        <div className="text-center pt-1">
          <Button size="sm" variant="ghost" onClick={() => setCollapsed(true)}
                  data-testid="ei-collapse-detailed"
                  className="text-[11px] text-slate-600">
            ▲ Collapse Detailed View
          </Button>
        </div>
      )}

      {/* Monthly Sales & Expense Comparison — always visible below EI */}
      <MonthlyComparisonCard
        session={session}
        center={center}
        currency={currency}
        currentMonth={month}
      />


      {/* Edit Dialog — admin only */}
      {canEdit && (
        <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
          <DialogContent data-testid="ei-edit-dialog">
            <DialogHeader>
              <DialogTitle>Edit Benchmark — {editRow?.head}</DialogTitle>
            </DialogHeader>
            {editRow && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Benchmark %</Label>
                  <Input type="number" step="0.1" value={editRow.benchmark_pct}
                    onChange={(e) => setEditRow((r) => ({ ...r, benchmark_pct: parseFloat(e.target.value) }))}
                    data-testid="ei-input-benchmark" />
                </div>
                <div>
                  <Label>Market Standard %</Label>
                  <Input type="number" step="0.1" value={editRow.market_std_pct}
                    onChange={(e) => setEditRow((r) => ({ ...r, market_std_pct: parseFloat(e.target.value) }))}
                    data-testid="ei-input-market" />
                </div>
                <div>
                  <Label>Approved Target %</Label>
                  <Input type="number" step="0.1" value={editRow.approved_target_pct}
                    onChange={(e) => setEditRow((r) => ({ ...r, approved_target_pct: parseFloat(e.target.value) }))}
                    data-testid="ei-input-target" />
                </div>
                <div>
                  <Label>Classification</Label>
                  <Select value={editRow.classification} onValueChange={(v) => setEditRow((r) => ({ ...r, classification: v }))}>
                    <SelectTrigger data-testid="ei-input-class"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Notes / Remarks</Label>
                  <Textarea rows={2} value={editRow.remarks}
                    onChange={(e) => setEditRow((r) => ({ ...r, remarks: e.target.value }))}
                    data-testid="ei-input-remarks" />
                </div>
                <div className="col-span-2">
                  <Label>Reason for change * <span className="text-[10px] text-slate-500">(audit trail)</span></Label>
                  <Textarea rows={2} value={editRow.reason} placeholder="e.g. Q1 benchmark refresh per management review"
                    onChange={(e) => setEditRow((r) => ({ ...r, reason: e.target.value }))}
                    data-testid="ei-input-reason" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} data-testid="ei-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Audit Trail Dialog — admin only */}
      {canEdit && (
        <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
          <DialogContent className="max-w-3xl" data-testid="ei-audit-dialog">
            <DialogHeader><DialogTitle>Audit Trail — {center}</DialogTitle></DialogHeader>
            {auditLoading ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
            ) : auditRows.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">No changes recorded yet.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Head</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>Old</TableHead>
                      <TableHead>New</TableHead>
                      <TableHead>By</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditRows.map((r) => (
                      <TableRow key={r.audit_id}>
                        <TableCell className="text-[11px]">{new Date(r.changed_at).toLocaleString()}</TableCell>
                        <TableCell className="font-medium">{r.head}</TableCell>
                        <TableCell>{r.field}</TableCell>
                        <TableCell className="text-slate-500">{String(r.old_value ?? "—").slice(0, 40)}</TableCell>
                        <TableCell className="font-medium">{String(r.new_value ?? "—").slice(0, 40)}</TableCell>
                        <TableCell>{r.changed_by}</TableCell>
                        <TableCell className="text-slate-600 max-w-[200px] truncate" title={r.reason}>{r.reason || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Diagnose (Why is this 0?) dialog ─────────────────────── */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-2xl" data-testid="ei-diagnose-dialog">
          <DialogHeader>
            <DialogTitle>Why is this 0?</DialogTitle>
            <DialogDescription>
              Diagnostic view for a single expense head — visible to Management only.
            </DialogDescription>
          </DialogHeader>
          {diagLoading && <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" />}
          {diagData && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Head:</span> <span className="font-semibold">{diagData.canonical_head}</span></div>
                <div><span className="text-slate-500">Center:</span> {diagData.center}</div>
                <div><span className="text-slate-500">Range:</span> {diagData.range?.from} → {diagData.range?.to || diagData.range?.end_exclusive}</div>
                <div><span className="text-slate-500">Total in range:</span> <span className="font-semibold">{fmt(diagData.total_amount, currency)}</span></div>
                <div><span className="text-slate-500">Matching txns:</span> {diagData.matching_count}</div>
                <div><span className="text-slate-500">Case/spelling variants:</span> {diagData.similar_spelling_count}</div>
                <div><span className="text-slate-500">Excluded (other heads) in range:</span> {diagData.excluded_count}</div>
              </div>
              {diagData.matching_sample?.length > 0 && (
                <div>
                  <p className="font-semibold mt-2">Matching transactions (sample)</p>
                  <Table className="text-xs"><TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Raw head</TableHead>
                    <TableHead className="text-right">Amount</TableHead><TableHead>Vendor</TableHead>
                  </TableRow></TableHeader><TableBody>
                    {diagData.matching_sample.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>{t.date}</TableCell><TableCell>{t.raw_head}</TableCell>
                        <TableCell className="text-right">{fmt(t.amount, currency)}</TableCell>
                        <TableCell className="text-slate-600">{t.vendor || t.note || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                </div>
              )}
              {diagData.last_known_transactions?.length > 0 && (
                <div>
                  <p className="font-semibold mt-2">Last known transactions (any date)</p>
                  <ul className="list-disc pl-5 text-slate-600">
                    {diagData.last_known_transactions.map((t, i) => (
                      <li key={i}>{t.date} — {t.expense_type} — {fmt(t.amount, currency)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {diagData.matching_count === 0 && diagData.last_known_transactions?.length === 0 && (
                <p className="text-slate-500 italic">No transactions found for this head — anywhere in the DB — for this center. Check if the head was renamed or entries are booked under a different name.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Average-Calculation Diagnostic (Super Admin) ────────── */}
      <Dialog open={avgDiagOpen} onOpenChange={setAvgDiagOpen}>
        <DialogContent className="max-w-2xl" data-testid="ei-avg-diag-dialog">
          <DialogHeader>
            <DialogTitle>Average Monthly Expenses — Calculation Breakdown</DialogTitle>
            <DialogDescription>
              Read-only diagnostic. Confirms the divisor equals the calendar
              window size, not the count of months with data. Zero-expense
              months are included as 0.00.
            </DialogDescription>
          </DialogHeader>
          {avgDiagLoading && <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" />}
          {avgDiagData && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2 pb-2 border-b">
                <div><span className="text-slate-500">Center:</span> <span className="font-semibold">{avgDiagData.center}</span></div>
                <div><span className="text-slate-500">Current Month:</span> <span className="font-semibold">{avgDiagData.current_month}</span></div>
                <div><span className="text-slate-500">Average Based On:</span> Last {avgDiagData.avg_window} Months</div>
                <div>
                  <span className="text-slate-500">Reconciled:</span>{" "}
                  <span className={avgDiagData.reconciled ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
                    {avgDiagData.reconciled ? "✓ YES" : `✗ NO (gap ${avgDiagData.reconcile_gap})`}
                  </span>
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto border rounded">
                <Table className="text-xs">
                  <TableHeader className="bg-slate-50 sticky top-0">
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Expense Total</TableHead>
                      <TableHead className="text-right">Entries</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(avgDiagData.per_month || []).map((m) => (
                      <TableRow key={m.month} data-testid={`ei-avg-diag-row-${m.month}`}>
                        <TableCell className="font-mono">{m.month}</TableCell>
                        <TableCell className={`text-right font-mono ${m.total === 0 ? "text-amber-700 italic" : ""}`}>
                          {fmt(m.total, currency)}
                        </TableCell>
                        <TableCell className="text-right text-slate-500">{m.entries}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Period Total:</span>
                  <span className="font-mono font-semibold">{fmt(avgDiagData.period_total, currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Divided By:</span>
                  <span className="font-mono font-semibold">{avgDiagData.divisor}</span></div>
                <div className="flex justify-between text-base"><span className="text-slate-700 font-semibold">Monthly Average:</span>
                  <span className="font-mono font-bold text-[#8B0000]" data-testid="ei-avg-diag-result">{fmt(avgDiagData.monthly_average, currency)}</span></div>
                <div className="text-[10px] text-slate-500 italic pt-1">{avgDiagData.note}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvgDiagOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ── Repair Heads dialog ─────────────────────────────────── */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-3xl" data-testid="ei-repair-dialog">
          <DialogHeader>
            <DialogTitle>Expense Head Mapping — Diagnostic Report</DialogTitle>
            <DialogDescription>
              Read-only scan. Nothing is deleted or modified. Use this to spot
              renamed / mis-spelled / unmapped heads across all historical data.
            </DialogDescription>
          </DialogHeader>
          {repairLoading && <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" />}
          {repairData && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-3 gap-2 pb-2 border-b">
                <div><span className="text-slate-500">Scope:</span> {repairData.scope_center}</div>
                <div><span className="text-slate-500">Scanned records:</span> {repairData.scanned_records}</div>
                <div><span className="text-slate-500">Master heads:</span> {repairData.master_head_count}</div>
                <div><span className="text-slate-500">Correctly mapped:</span> <span className="font-semibold text-emerald-700">{repairData.counts?.correctly_mapped}</span></div>
                <div><span className="text-slate-500">Auto-repaired (case fixed):</span> <span className="font-semibold text-sky-700">{repairData.counts?.auto_repaired}</span></div>
                <div><span className="text-slate-500">Unmatched:</span> <span className="font-semibold text-rose-700">{repairData.counts?.unmatched}</span></div>
                <div><span className="text-slate-500">Duplicates w/ variants:</span> {repairData.counts?.duplicates_with_variants}</div>
                <div><span className="text-slate-500">Zero-value heads:</span> {repairData.counts?.zero_value_heads}</div>
                <div><span className="text-slate-500">Records missing head:</span> <span className={repairData.counts?.records_missing_head ? "text-rose-700 font-semibold" : ""}>{repairData.counts?.records_missing_head}</span></div>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <Table className="text-xs">
                  <TableHeader><TableRow>
                    <TableHead>Head</TableHead><TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead>Status</TableHead><TableHead>Variants</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {repairData.heads?.slice(0, 100).map((h) => (
                      <TableRow key={h.canonical_head}>
                        <TableCell className="font-medium">{h.canonical_head}</TableCell>
                        <TableCell className="text-right">{fmt(h.total_amount, currency)}</TableCell>
                        <TableCell className="text-right">{h.record_count}</TableCell>
                        <TableCell>
                          <Badge className={
                            h.status === "correctly_mapped" ? "bg-emerald-500/20 text-emerald-700 border-emerald-500/40" :
                            h.status === "auto_repaired" ? "bg-sky-500/20 text-sky-700 border-sky-500/40" :
                            "bg-rose-500/20 text-rose-700 border-rose-500/40"
                          }>{h.status}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 max-w-[220px] truncate">
                          {h.raw_name_variants?.map(v => `${v.name}(${v.count})`).join(", ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Reconcile diagnostic (admin) ─────────────────────────── */}
      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="max-w-2xl" data-testid="ei-reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Reconcile with Center Accounts</DialogTitle>
            <DialogDescription>
              Backend uses a single source of truth (<code>/api/expense-intelligence/list</code>).
              Any two surfaces querying the same center + date range MUST produce the same
              numbers. This panel shows the exact request parameters currently in play so you
              can compare with what Center Accounts is sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-2 border rounded p-3 bg-slate-50/60">
              <div><span className="text-slate-500">Surface:</span> <span className="font-semibold">{storageKey}</span></div>
              <div><span className="text-slate-500">Center:</span> <span className="font-semibold">{center}</span></div>
              <div><span className="text-slate-500">Current Month:</span> <span className="font-semibold">{month}</span></div>
              <div><span className="text-slate-500">Simplified mode:</span> {simplified ? "true" : "false"}</div>
              <div><span className="text-slate-500">Average window:</span> <span className="font-semibold">{summary.avg_window_label || avgWindow}</span></div>
              <div><span className="text-slate-500">Total (current month):</span> <span className="font-semibold">{fmt(data?.expenses_total, currency)}</span></div>
            </div>
            <div className="p-2 rounded bg-emerald-50 border border-emerald-300 text-emerald-800">
              ✓ Current Month is <b>{month}</b> (full accounting month — never derived from a
              custom range). Any other EI surface pointed at the same center &amp; month will
              show identical totals — both call the same backend engine
              <code className="mx-1">/api/expense-intelligence/list</code>.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (!strip) return body;
  return (
    <SummaryStrip Icon={Layers} title="Expense Intelligence & Average Cost" desc="Expense analysis, trends and savings insights." basis={`Basis: ${month} vs ${summary.avg_window_label || "average"}`}
      chips={metrics.length ? metrics.map((m) => <MetalChip key={m.k} label={m.label} value={m.value} sub={m.sub} tone={m.tone} testid={`or-ei-chip-${m.k}`} />)
        : <div className="col-span-full text-base text-[#5a3a22]">{loading ? "Loading…" : "No expense data for this period."}</div>}
      open={!collapsed} onToggle={() => setCollapsed(c => !c)} detailsLabel="Expense Head Details (Grid)" testid="or-ei-strip">
      {body}
    </SummaryStrip>
  );
}

function SummaryTile({ label, value, tone = "slate", testid }) {
  const toneMap = {
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    sky:     "bg-sky-50 border-sky-200 text-sky-800",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-800",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <Card className={`border ${toneMap[tone]}`} data-testid={testid}>
      <CardContent className="p-3">
        <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
        <p className="text-lg font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function TotalCell({ label, value, sub, tone, bold }) {
  const toneCls = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-800";
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-lg mt-0.5 ${bold ? "font-black" : "font-bold"} ${toneCls}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
